import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import {
  CalendarClock,
  Loader2,
  RefreshCw,
  MapPin,
  Clock,
  CheckSquare,
  Square,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  List,
} from 'lucide-react';
import { usePageState } from '../hooks/usePageState';
import { RoleGuard, AccessDenied, useRoleCheck } from '../components/RoleGuard';

interface IcloudEvent {
  id: string;
  calendar_name: string | null;
  title: string | null;
  description: string | null;
  location: string | null;
  start_time: string | null;
  end_time: string | null;
  due_time: string | null;
  all_day: boolean;
  is_reminder: boolean;
  completed: boolean;
  status: string | null;
  client_name: string | null;
}

interface IcloudStatus {
  configured: boolean;
  syncEnabled: boolean;
  lastSync: {
    started_at: string;
    finished_at: string | null;
    success: boolean;
    events_synced: number;
    reminders_synced: number;
    error_message: string | null;
  } | null;
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('bymarke_token')}`,
});

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

// Chiave giorno in ora locale (browser) — coerente tra eventi e celle del calendario.
const monthKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const monthFromKey = (key: string) => {
  const [year, month] = key.split('-').map((n) => parseInt(n, 10));
  if (!year || !month) return new Date();
  return new Date(year, month - 1, 1);
};

const localKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fmtDayLabel = (d: Date) =>
  d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

const fmtTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';

const fmtDateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : '—';

const Appuntamenti: React.FC = () => {
  const { isAdmin } = useRoleCheck();
  const [events, setEvents] = useState<IcloudEvent[]>([]);
  const [status, setStatus] = useState<IcloudStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // Vista, mese e giorno selezionato stanno nell'URL, così indietro e refresh
  // riaprono la stessa schermata.
  const [pageState, setPageState] = usePageState(
    { view: 'calendario', mese: monthKey(new Date()), giorno: localKey(new Date()) },
    { push: ['view'] }
  );
  const view = pageState.view as 'calendario' | 'lista';
  const setView = (value: 'calendario' | 'lista') => setPageState({ view: value });
  const viewDate = useMemo(() => monthFromKey(pageState.mese), [pageState.mese]);
  const setViewDate = (updater: (current: Date) => Date) =>
    setPageState({ mese: monthKey(updater(viewDate)) });
  const selectedDay = pageState.giorno || null;
  const setSelectedDay = (day: string | null) => setPageState({ giorno: day ?? '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [apptRes, statusRes] = await Promise.all([
        fetch('/api/icloud/appointments', { headers: authHeaders() }),
        fetch('/api/icloud/status', { headers: authHeaders() }),
      ]);
      const apptData = await apptRes.json();
      const statusData = await statusRes.json();
      if (apptData.success) setEvents(apptData.data);
      if (statusData.success) setStatus(statusData.data);
    } catch {
      toast.error('Errore nel caricamento degli appuntamenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch('/api/icloud/sync', { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Sincronizzazione completata');
        await load();
      } else {
        toast.error(data.message || 'Sincronizzazione fallita');
      }
    } catch {
      toast.error('Errore durante la sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  // Eventi datati indicizzati per giorno locale.
  const eventsByDay = useMemo(() => {
    const map: Record<string, IcloudEvent[]> = {};
    for (const e of events) {
      if (e.is_reminder || !e.start_time) continue;
      const k = localKey(new Date(e.start_time));
      (map[k] = map[k] || []).push(e);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.start_time! < b.start_time! ? -1 : 1));
    }
    return map;
  }, [events]);

  // Griglia del mese: 6 settimane (42 celle), lunedì come primo giorno.
  const grid = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7; // Lun=0
    const start = new Date(year, month, 1 - offset);
    const cells: { date: Date; inMonth: boolean; key: string }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push({ date: d, inMonth: d.getMonth() === month, key: localKey(d) });
    }
    return cells;
  }, [viewDate]);

  const reminders = useMemo(() => events.filter((e) => e.is_reminder), [events]);
  const todayKey = localKey(new Date());
  const selectedEvents = selectedDay ? eventsByDay[selectedDay] || [] : [];

  const goMonth = (delta: number) =>
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));

  return (
    <RoleGuard
      allowedRoles={['admin', 'videomaker', 'video_editor', 'copywriter']}
      fallback={<AccessDenied />}
    >
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#fdf751]/20 flex items-center justify-center">
              <CalendarClock className="w-6 h-6 text-[#b8a800]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Appuntamenti</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Riprese dei videomaker da iCloud.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Toggle vista */}
            <div className="inline-flex rounded-xl bg-slate-100 dark:bg-gray-800 p-1">
              <button
                type="button"
                onClick={() => setView('calendario')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  view === 'calendario'
                    ? 'bg-white dark:bg-gray-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                <CalendarDays className="w-4 h-4" /> Calendario
              </button>
              <button
                type="button"
                onClick={() => setView('lista')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  view === 'lista'
                    ? 'bg-white dark:bg-gray-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 dark:text-slate-400'
                }`}
              >
                <List className="w-4 h-4" /> Lista
              </button>
            </div>

            {isAdmin() && (
              <button
                type="button"
                onClick={runSync}
                disabled={syncing || !status?.configured}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{syncing ? 'Sincronizzo…' : 'Sincronizza'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Banner: integrazione non configurata */}
        {status && !status.configured && (
          <div className="flex items-start gap-3 p-4 mb-6 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <div className="font-semibold">Integrazione iCloud non attiva</div>
              <div className="mt-0.5">Configura le credenziali iCloud nel backend e riavvia il servizio.</div>
            </div>
          </div>
        )}

        {/* Stato ultima sincronizzazione */}
        {status?.configured && status.lastSync && (
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            Ultimo aggiornamento: {fmtDateTime(status.lastSync.finished_at || status.lastSync.started_at)}
            {status.lastSync.success
              ? ` · ${status.lastSync.events_synced} appuntamenti`
              : ` · errore: ${status.lastSync.error_message || 'sconosciuto'}`}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : view === 'calendario' ? (
          <>
            {/* Barra mese */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => goMonth(-1)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 text-slate-600 dark:text-slate-300"
                aria-label="Mese precedente"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                  {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    setPageState({ mese: monthKey(now), giorno: localKey(now) });
                  }}
                  className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-gray-700"
                >
                  Oggi
                </button>
              </div>
              <button
                type="button"
                onClick={() => goMonth(1)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 text-slate-600 dark:text-slate-300"
                aria-label="Mese successivo"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Intestazione giorni */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAYS.map((w) => (
                <div key={w} className="text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 py-1">
                  {w}
                </div>
              ))}
            </div>

            {/* Griglia */}
            <div className="grid grid-cols-7 gap-1">
              {grid.map((cell) => {
                const dayEvents = eventsByDay[cell.key] || [];
                const isToday = cell.key === todayKey;
                const isSelected = cell.key === selectedDay;
                return (
                  <button
                    type="button"
                    key={cell.key}
                    onClick={() => setSelectedDay(cell.key)}
                    className={`min-h-[76px] sm:min-h-[92px] p-1.5 rounded-lg border text-left align-top transition ${
                      isSelected
                        ? 'border-[#b8a800] ring-1 ring-[#b8a800]/40'
                        : 'border-slate-100 dark:border-gray-700'
                    } ${
                      cell.inMonth
                        ? 'bg-white dark:bg-gray-800'
                        : 'bg-slate-50 dark:bg-gray-900/40'
                    } hover:border-slate-300 dark:hover:border-gray-600`}
                  >
                    <div
                      className={`flex items-center justify-center w-6 h-6 mb-1 text-xs font-semibold rounded-full ${
                        isToday
                          ? 'bg-[#fdf751] text-slate-900'
                          : cell.inMonth
                          ? 'text-slate-700 dark:text-slate-200'
                          : 'text-slate-300 dark:text-slate-600'
                      }`}
                    >
                      {cell.date.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((e) => (
                        <div
                          key={e.id}
                          title={`${fmtTime(e.start_time)} ${e.title || ''}`}
                          className="truncate text-[10px] leading-tight px-1 py-0.5 rounded bg-[#fdf751]/25 text-[#8a7f00] dark:text-[#d9c400]"
                        >
                          {!e.all_day && <span className="font-semibold">{fmtTime(e.start_time)} </span>}
                          {e.title || 'Senza titolo'}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-slate-400 dark:text-slate-500 px-1">
                          +{dayEvents.length - 3}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Dettaglio giorno selezionato */}
            {selectedDay && (
              <div className="mt-6">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3 capitalize">
                  {fmtDayLabel(new Date(selectedDay + 'T00:00:00'))}
                </h3>
                {selectedEvents.length === 0 ? (
                  <div className="text-sm text-slate-400 dark:text-slate-500 py-4">
                    Nessun appuntamento in questo giorno.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedEvents.map((e) => (
                      <EventCard key={e.id} e={e} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* ===== VISTA LISTA ===== */
          <ListView eventsByDay={eventsByDay} reminders={reminders} />
        )}
      </div>
    </RoleGuard>
  );
};

const EventCard: React.FC<{ e: IcloudEvent }> = ({ e }) => (
  <div className="flex gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-slate-100 dark:border-gray-700 shadow-sm">
    <div className="flex flex-col items-center justify-center px-2 min-w-[64px] text-center border-r border-slate-100 dark:border-gray-700">
      {e.all_day ? (
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Tutto il giorno</span>
      ) : (
        <>
          <span className="text-sm font-bold text-slate-900 dark:text-white">{fmtTime(e.start_time)}</span>
          {e.end_time && (
            <span className="text-[11px] text-slate-400 dark:text-slate-500">{fmtTime(e.end_time)}</span>
          )}
        </>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-slate-900 dark:text-white">{e.title || 'Senza titolo'}</div>
      {e.location && (
        <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mt-1">
          <MapPin className="w-3.5 h-3.5" /> {e.location}
        </div>
      )}
      {e.description && (
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{e.description}</div>
      )}
      <div className="flex items-center gap-2 mt-1.5">
        {e.calendar_name && (
          <span className="text-[11px] text-slate-400 dark:text-slate-500">{e.calendar_name}</span>
        )}
        {e.client_name && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#fdf751]/20 text-[#b8a800] font-medium">
            {e.client_name}
          </span>
        )}
      </div>
    </div>
  </div>
);

const ListView: React.FC<{
  eventsByDay: Record<string, IcloudEvent[]>;
  reminders: IcloudEvent[];
}> = ({ eventsByDay, reminders }) => {
  const days = Object.keys(eventsByDay).sort();
  if (days.length === 0 && reminders.length === 0) {
    return (
      <div className="text-center py-20 text-slate-400 dark:text-slate-500">
        <CalendarClock className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>Nessun appuntamento da mostrare.</p>
      </div>
    );
  }
  return (
    <div className="space-y-8">
      {reminders.length > 0 && (
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3">
            Promemoria
          </h2>
          <div className="space-y-2">
            {reminders.map((r) => (
              <div
                key={r.id}
                className="flex items-start gap-3 p-3.5 bg-white dark:bg-gray-800 rounded-xl border border-slate-100 dark:border-gray-700"
              >
                {r.completed ? (
                  <CheckSquare className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <Square className="w-5 h-5 text-slate-300 dark:text-slate-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${r.completed ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>
                    {r.title || 'Senza titolo'}
                  </div>
                  {r.due_time && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Scadenza: {fmtDateTime(r.due_time)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {days.map((k) => (
        <section key={k}>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3 capitalize">
            {fmtDayLabel(new Date(k + 'T00:00:00'))}
          </h2>
          <div className="space-y-2">
            {eventsByDay[k].map((e) => (
              <EventCard key={e.id} e={e} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default Appuntamenti;
